import { useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "../api";
import { AddForm, ConfirmDialog, SectionHead } from "./Section";
import { useQueryParam } from "../urlState";

// Scoring, the fourth product (docs/SCORING_WORKBENCH.md, "Screens"): read-only for now. How every
// trait is scored — stages A–H as one rater runs them — and how a rater's scores compare with human
// scores. Editing, runs and promotion come later; this shows what exists and says plainly what does
// not ("Not available · why", "not built yet").
//
// The selected trait and experiment live in the query string (docs/DESIGN.md, "Links and the URL"), so a
// view can be linked and survives a reload.

// ------------------------------------------------------------------------------------ types

export interface Trait { trait_id: string; label: string; scale: number[]; kind: string;
                         published_version: number | null }
export interface Rubric { rubric_id: string; name: string; publisher: string | null;
                          trait_term: string | null; status: string; traits: Trait[] }
interface Level { level: number; descriptor: string | string[] | null; stacked: boolean }
interface Template { version: string; sha256: string; body: string; status: string }
export interface Stage {
  key: string; name: string; kind: "data" | "llm" | "rule"; prompt_stage?: string;
  template?: Template | null; model?: string; effort?: string | null; sees?: string[];
  detail?: Record<string, unknown>;
  /** The trait set this itself, in place of the rater's own (0106). */
  own_model?: boolean; own_absence?: boolean;
}
interface Segment { name: string; source: string; used: boolean; note?: string }
interface TraitView {
  available: boolean;
  trait: { trait_id: string; label: string; standard: string | null; grade_band: string | null;
           version: number | null; levels: Level[] };
  rubrics: { rubric_id: string; name: string }[];
  prompts: { task_id: string; name: string; has_statement: boolean; text_count: number }[];
  rater: { config_id: string; config_key: string; version: number; status: string;
           model_id: string; level_method: string } | null;
  stages: Stage[];
  load: { shared: Segment[]; trait: Segment[] } | null;
}
export interface Rater { config_id: string; config_key: string; version: number; status: string;
                         model_id: string; effort: string | null; level_method: string;
                         level_threshold: number; context: unknown;
                         promoted_by: string | null; promoted_at: string | null;
                         papers_scored: number; traits_with_own?: number;
                         fit_gate?: string | null; scorer?: string | null;
                         fit_gate_id?: string | null; scorer_id?: string | null }
export interface Metric { trait_id: string; trait_label: string | null; metric: string;
                          reference: string; subgroup: string; available: boolean;
                          value: number | null; ci_low: number | null; ci_high: number | null;
                          n: number; unavailable_reason: string | null }
export interface Policy { metric: string; comparator: ">=" | "<=" | "abs<="; value: number;
                          min_n: number; reference: string; citation: string }

// ------------------------------------------------------------------------------ pure helpers

/** Which reference a threshold is judged on: the composite when it has a value, otherwise the
 *  single human score; degradation exists only against the first human. The same rule as
 *  `writing.measurement.scorecard.judged`. */
export function judgedRow(rows: Metric[], node: string, metric: string): Metric | undefined {
  const mine = rows.filter((r) => r.trait_id === node && r.metric === metric && r.subgroup === "all");
  return mine.find((r) => r.reference === "composite" && r.available)
    ?? mine.find((r) => r.reference === "single")
    ?? mine.find((r) => r.reference === "composite")
    ?? mine.find((r) => r.reference === "none");
}

export type Verdict = "meets" | "does not meet" | "not available" | "";

/** The verdict for one row against the active policy — `scorecard.verdict`'s rule. */
export function verdict(row: Metric | undefined, policy: Policy[]): Verdict {
  if (!row) return "";
  const rule = policy.find((p) => p.metric === row.metric);
  if (!rule) return "";
  if (!row.available || row.value === null) return "not available";
  const v = row.value, limit = rule.value;
  const ok = rule.comparator === ">=" ? v >= limit
    : rule.comparator === "<=" ? v <= limit : Math.abs(v) <= limit;
  return ok ? "meets" : "does not meet";
}

/** A metric as it reads in a cell: the number and its interval, or "Not available". */
export function cell(row: Metric | undefined, digits = 2): string {
  if (!row) return "—";
  if (!row.available || row.value === null) return "Not available";
  const ci = row.ci_low !== null && row.ci_high !== null
    ? ` [${row.ci_low.toFixed(digits)}, ${row.ci_high.toFixed(digits)}]` : "";
  return `${row.value.toFixed(digits)}${ci}`;
}

/** The traits an experiment has rows for, in label order. */
export function traitsOf(rows: Metric[]): { trait_id: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const r of rows) if (!seen.has(r.trait_id)) seen.set(r.trait_id, r.trait_label ?? r.trait_id);
  return [...seen.entries()].map(([trait_id, label]) => ({ trait_id, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** One stage setting as it reads: lists joined, a missing value as a dash, the resolver as
 *  built or not, notes as "label: note" pairs. */
export function detailText(key: string, val: unknown): string {
  if (val === null || val === undefined || val === "") return "—";
  if (typeof val === "boolean") return key === "resolver" ? (val ? "built" : "not built yet")
                                                          : val ? "yes" : "no";
  if (Array.isArray(val)) return val.length ? val.map(String).join(", ") : "—";
  if (typeof val === "object") {
    const parts = Object.entries(val as Record<string, unknown>).map(([k, v]) => `${k}: ${String(v)}`);
    return parts.length ? parts.join(" · ") : "—";
  }
  return String(val);
}

/** What a rater is called on screen: "writing-default v1". */
export const raterName = (r: { config_key: string; version: number }) => `${r.config_key} v${r.version}`;
/** What a scoring method is called on screen (docs/GLOSSARY.md); `level_method` is the stored value. */
export const methodName = (m: string) =>
  ({ one_level_call: "one level call", level_ladder: "level ladder",
     pairwise_anchors: "pairwise against anchors" } as Record<string, string>)[m] ?? m;
/** The prompt stage a rater's E judges with. */
const judgeOf = (m: string) => (m === "level_ladder" ? "level" : m === "pairwise_anchors" ? "compare" : "score");

// ------------------------------------------------------------------------------- loading

type Load<T> = { data: T | null; error: string | null };

function useGet<T>(path: string | null): Load<T> {
  const [state, setState] = useState<Load<T>>({ data: null, error: null });
  useEffect(() => {
    if (!path) { setState({ data: null, error: null }); return; }
    let live = true;
    setState({ data: null, error: null });
    api.get<T>(path).then(
      (data) => { if (live) setState({ data, error: null }); },
      (e) => { if (live) setState({ data: null,
        error: e instanceof ApiError ? (e.detail ?? e.message) : "That could not be read." }); });
    return () => { live = false; };
  }, [path]);
  return state;
}

/** What to show instead of a screen that has nothing to draw yet: the error, loading, or
 *  "not loaded" when the tables are missing. Null once there is data. A function, not a
 *  component, so a caller can test it: an element is always truthy. */
function notReady<T extends { available?: boolean }>(load: Load<T>, what: string): ReactNode {
  if (load.error) return <p className="inc-error">{load.error}</p>;
  if (!load.data) return <p className="muted dots">loading {what}</p>;
  if (load.data.available === false) return <p className="sc-mut">Not loaded yet: the {what} tables are not in this database.</p>;
  return null;
}

// ---------------------------------------------------------------------------- Rubrics

export function ScoringRubrics() {
  const load = useGet<{ available: boolean; rubrics: Rubric[] }>("/scoring/rubrics");
  const [trait, choose] = useQueryParam("trait");
  const status = notReady(load, "rubrics");
  return (
    <div className="gl sc">
      <h2>Rubrics</h2>
      <p className="gl-lede">Every rubric and its traits. Open a trait to see how it is scored, stage by stage.</p>
      <div className="sc-two">
        <nav className="sc-tree" aria-label="Rubrics and traits">
          {status ?? (load.data!.rubrics.length === 0 ? <p className="sc-mut">None yet.</p> :
            load.data!.rubrics.map((r) => (
              <details key={r.rubric_id} open={r.traits.some((t) => t.trait_id === trait)}>
                <summary>{r.name}{r.status !== "published" &&
                  <span className="sc-chip">{r.status}</span>}</summary>
                <ul>
                  {r.traits.map((t) => (
                    <li key={t.trait_id}>
                      <button type="button" className={"sc-trait" + (t.trait_id === trait ? " cur" : "")}
                              aria-current={t.trait_id === trait ? "page" : undefined}
                              onClick={() => choose(t.trait_id)}>
                        {t.label}
                        {t.published_version === null && <span className="sc-mut"> · no wording</span>}
                      </button>
                    </li>))}
                  {r.traits.length === 0 && <li className="sc-mut">No traits.</li>}
                </ul>
              </details>)))}
        </nav>
        <section className="sc-pane">
          {trait ? <TraitPanel traitId={trait} />
                 : <p className="sc-mut">Choose a trait on the left.</p>}
        </section>
      </div>
    </div>
  );
}

function TraitPanel({ traitId }: { traitId: string }) {
  const raters = useGet<{ available: boolean; raters: Rater[] }>("/scoring/raters");
  const [rater, setRater] = useQueryParam("rater");
  const path = `/scoring/traits/${encodeURIComponent(traitId)}`
    + (rater ? `?rater=${encodeURIComponent(rater)}` : "");
  const load = useGet<TraitView>(path);
  const [stageKey, setStageKey] = useState("A");
  const status = notReady(load, "trait");
  if (status) return status;
  const v = load.data!;
  const stage = v.stages.find((s) => s.key === stageKey);
  return (
    <div>
      <div className="sc-head">
        <h3 className="sc-title">{v.trait.label}</h3>
        <span className="sc-mut">
          Scale {v.trait.levels[0]?.level}–{v.trait.levels[v.trait.levels.length - 1]?.level}
          {" · "}{v.trait.version ? `wording v${v.trait.version}` : "no published wording"}
          {v.rubrics.length > 0 && ` · in ${v.rubrics.map((r) => r.name).join(", ")}`}
        </span>
        <label className="sc-rater">Rater{" "}
          <select id="sc-rater" value={rater ?? v.rater?.config_id ?? ""}
                  onChange={(e) => setRater(e.target.value, { replace: true })}>
            {!v.rater && <option value="">No active rater</option>}
            {(raters.data?.raters ?? []).map((r) => (
              <option key={r.config_id} value={r.config_id}>{raterName(r)} · {r.status}</option>))}
          </select>
        </label>
      </div>
      {!v.rater ? <p className="sc-mut">No rater to show. Choose one above.</p> : (
        <>
          <ol className="sc-stages" aria-label="Stages">
            {v.stages.map((s) => (
              <li key={s.key}>
                <button type="button" className={"sc-stage" + (s.key === stageKey ? " cur" : "")}
                        aria-current={s.key === stageKey ? "step" : undefined}
                        onClick={() => setStageKey(s.key)}>
                  <span className="k">{s.key}{s.kind === "llm" && <span className="sc-llm">LLM</span>}</span>
                  <span className="n">{s.name}</span>
                </button>
              </li>))}
          </ol>
          {stageKey === "A" && v.load ? <LoadDiagram load={v.load} /> : stage && <StageDetail stage={stage} />}
          <h4>Levels</h4>
          <table className="tbl sc-levels">
            <thead><tr><th>Level</th><th>What it says</th></tr></thead>
            <tbody>{v.trait.levels.map((l) => (
              <tr key={l.level}><td>{l.level}</td><td>{
                l.descriptor === null ? <span className="sc-mut">No wording for this level.</span>
                : Array.isArray(l.descriptor) ? <ul>{l.descriptor.map((d, i) => <li key={i}>{d}</li>)}</ul>
                : l.descriptor}</td></tr>))}</tbody>
          </table>
          <h4>Prompts scored on this trait</h4>
          {v.prompts.length === 0 ? <p className="sc-mut">None yet.</p> : (
            <ul>{v.prompts.map((p) => (
              <li key={p.task_id}>{p.name} · {p.has_statement ? "statement entered" : "no statement yet"}
                {" · "}{p.text_count === 1 ? "1 text" : `${p.text_count} texts`}</li>))}</ul>)}
        </>)}
    </div>
  );
}

function LoadDiagram({ load }: { load: { shared: Segment[]; trait: Segment[] } }) {
  const seg = (s: Segment) => (
    <div key={s.name} className={"sc-seg" + (s.used ? "" : " off")}>
      <b>{s.name}</b> <span className="sc-mut">{s.used ? "used" : "not used"}</span>
      <div className="d">{s.source}{s.note ? ` · ${s.note}` : ""}</div>
    </div>);
  return (
    <div className="sc-load">
      <div className="sc-box shared"><h4>Shared by every trait of a paper</h4>{load.shared.map(seg)}</div>
      <div className="sc-box own"><h4>Only for this trait</h4>{load.trait.map(seg)}</div>
    </div>
  );
}

function StageDetail({ stage }: { stage: Stage }) {
  return (
    <div className="sc-detail">
      <h4>{stage.key} · {stage.name}</h4>
      {stage.prompt_stage && (
        <dl className="sc-kv">
          <dt>Model</dt><dd><span className="mono">{stage.model ?? "—"}</span>
            {stage.own_model && <span className="sc-own">set for this trait</span>}</dd>
          <dt>Effort</dt><dd>{stage.effort ?? "—"}</dd>
          <dt>Sees beyond its default</dt><dd>{stage.sees && stage.sees.length ? stage.sees.join(", ") : "nothing"}</dd>
          <dt>Template</dt><dd>{stage.template ? `${stage.template.version} · ${stage.template.sha256}` : "none linked"}</dd>
        </dl>)}
      {stage.detail && (
        <dl className="sc-kv">{Object.entries(stage.detail).map(([k, val]) => (
          <span key={k} className="sc-kv-row"><dt>{k.replace(/_/g, " ")}</dt>
            <dd>{detailText(k, val)}
              {k === "when absent" && stage.own_absence && <span className="sc-own">set for this trait</span>}</dd></span>))}
        </dl>)}
      {stage.template && <pre className="sc-prompt">{stage.template.body}</pre>}
      {stage.kind === "data" && !stage.detail && <p className="sc-mut">
        {stage.key === "H" ? "Every judgment is appended to the record in one transaction, and the paper moves on."
                           : "Assembles what the later stages may see."}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------- Raters, Models

export function ScoringRaters() {
  const [reload, setReload] = useState(0);
  const load = useGet<{ available: boolean; raters: Rater[] }>(`/scoring/raters?r=${reload}`);
  const [chosen, setChosen] = useQueryParam("rater");
  const [adding, setAdding] = useState(false);
  const can = useCan();
  const choose = (id: string) => setChosen(id);
  const changed = (id?: string) => { setAdding(false); setReload((n) => n + 1); if (id) choose(id); };
  const status = notReady(load, "raters");
  return (
    <div className="gl sc">
      <h2>Raters</h2>
      <p className="gl-lede">A rater is built from parts: a fit gate and a scorer, with load and write in code. A draft's parts can change until it scores its first paper; after that a change is a new version.</p>
      <div className="sc-head"><button type="button" className="btn sm" disabled={!can.maintain}
        title={can.maintain ? undefined : NOT_MAINTAINER} onClick={() => setAdding(true)}>New rater</button></div>
      {adding && <RaterForm onCancel={() => setAdding(false)} onDone={changed} />}
      {status ?? (load.data!.raters.length === 0 ? <p className="sc-mut">None yet.</p> : (
        <table className="tbl">
          <thead><tr><th>Rater</th><th>Status</th><th>Built from</th><th>Judge</th>
            <th>Per trait</th><th>Papers scored</th><th>Promoted by</th></tr></thead>
          <tbody>{load.data!.raters.map((r) => (
            <tr key={r.config_id} className={r.config_id === chosen ? "cur" : undefined}>
              <td><button type="button" className="btn-link" onClick={() => choose(r.config_id)}>
                {raterName(r)}</button><div className="d mono">{r.config_id.slice(0, 8)}</div></td>
              <td>{r.status}</td>
              <td>{r.fit_gate && r.scorer ? `${r.fit_gate} · ${r.scorer}`
                   : <span className="mono">{r.model_id}</span>}</td>
              <td>{methodName(r.level_method)}{r.level_method === "level_ladder" ? ` · threshold ${r.level_threshold}` : ""}</td>
              <td>{r.traits_with_own ? `${r.traits_with_own} set their own`
                                     : <span className="sc-mut">none</span>}</td>
              <td>{r.papers_scored}</td>
              <td>{r.promoted_by ?? <span className="sc-mut">—</span>}</td>
            </tr>))}</tbody>
        </table>))}
      {chosen && <RaterDetail key={`${chosen}:${reload}`} id={chosen} onChanged={changed} />}
    </div>
  );
}

interface TraitOwn { trait_id: string; label: string | null; models: Record<string, string>;
                     own_models: string[]; absence: string; own_absence: boolean }
interface RaterView {
  available: boolean;
  rater: { config_id: string; config_key: string; version: number; status: string;
           model_id: string; level_method: string; rationale: string | null;
           fit_gate: string | null; scorer: string | null; fit_gate_id: string | null;
           scorer_id: string | null; load_version: string; write_version: string };
  stages: Stage[]; traits: TraitOwn[];
}

const PER_PAPER = ["A", "B", "H"];

/** What a stage box says in one line: its model, or its rule. */
function stageLine(s: Stage): string {
  if (s.model) return s.model;
  const d = s.detail ?? {};
  if (s.key === "F") return d.method === "level ladder" ? `level ladder at ${d.threshold}`
    : String(d.method ?? "").startsWith("walk down") ? "walk down: beat 1 of 3" : "on-scale check";
  if (s.key === "G") return d.resolver ? "resolver" : `re-run ×${d.budget}`;
  if (s.key === "D") return `normalisation v${d.normalization_version}`;
  return s.key === "A" ? "paper, task, texts" : "score_event, scoring_trace";
}

// One rater: what runs once per paper (A, B, H), what runs once per trait (C–G, indented under
// it), and every trait that sets its own model or absence rule (0106) — so a rater reads as the
// pipeline it is, and a trait judged differently says so.
function RaterDetail({ id, onChanged }: { id: string; onChanged: (id?: string) => void }) {
  const load = useGet<RaterView>(`/scoring/raters/${encodeURIComponent(id)}`);
  const can = useCan();
  const [form, setForm] = useState<"new" | "change" | null>(null);
  const [promoting, setPromoting] = useState(false);
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const status = notReady(load, "rater");
  if (status) return status;
  const v = load.data!;
  const draft = v.rater.status === "draft";
  async function promote() {
    setBusy(true); setError(null);
    try {
      await api.post(`/scoring/raters/${encodeURIComponent(id)}/promote`, { rationale: why });
      setPromoting(false); onChanged(id);
    } catch (e) { setError(said(e)); } finally { setBusy(false); }
  }
  const judge = judgeOf(v.rater.level_method);
  const box = (s: Stage) => (
    <li key={s.key} className="sc-stage">
      <span className="k">{s.key} · {s.name}{s.kind === "llm" && <span className="sc-llm">LLM</span>}</span>
      <span className="n mono">{stageLine(s)}</span>
      {s.key === "E" && <span className="n">absent: {String(s.detail?.["when absent"] ?? "abstain")}</span>}
    </li>);
  return (
    <section className="sc-card sc-rater-detail">
      <div className="sc-head">
        <h3 className="sc-title">{raterName(v.rater)} <span className="sc-mut">· {v.rater.status}</span></h3>
        <button type="button" className="btn sm" disabled={!can.maintain || !v.rater.fit_gate_id}
                title={!can.maintain ? NOT_MAINTAINER : !v.rater.fit_gate_id ? "Made before parts: build a new rater instead." : undefined}
                onClick={() => setForm("new")}>New version</button>
        {draft && <button type="button" className="btn sm" disabled={!can.maintain}
                          title={can.maintain ? undefined : NOT_MAINTAINER}
                          onClick={() => setForm("change")}>Change parts</button>}
        {draft && <button type="button" className="btn sm primary" disabled={!can.lead}
                          title={can.lead ? undefined : NOT_LEAD}
                          onClick={() => setPromoting(true)}>Promote…</button>}
      </div>
      {v.rater.rationale && <p className="sc-mut">{v.rater.rationale}</p>}
      <p className="sc-mut">Built from {v.rater.fit_gate ?? "no fit gate"} · {v.rater.scorer ?? "no scorer"}
        {" "}· {v.rater.load_version} · {v.rater.write_version}
        {draft ? " · a draft: its parts can change until it scores its first paper" : ""}</p>
      {error && <p className="inc-error">{error}</p>}
      {form && <RaterForm rater={v.rater} change={form === "change"} onCancel={() => setForm(null)}
                          onDone={(newId) => { setForm(null); onChanged(newId); }} />}
      <ConfirmDialog open={promoting} danger={false} busy={busy || !why.trim()} confirmLabel="Promote"
                     title={`Promote ${raterName(v.rater)}?`} onConfirm={promote}
                     onCancel={() => setPromoting(false)}>
        <p>It becomes the active {v.rater.config_key} rater; the one it replaces is superseded.</p>
        <label>Why <input value={why} onChange={(e) => setWhy(e.target.value)}
                          placeholder="Cheaper fit gate, same judge" /></label>
      </ConfirmDialog>
      <h4>Once per paper</h4>
      <ol className="sc-strip">{v.stages.filter((s) => PER_PAPER.includes(s.key)).map(box)}</ol>
      <div className="sc-per-trait">
        <h4>Once per trait</h4>
        <ol className="sc-strip">{v.stages.filter((s) => !PER_PAPER.includes(s.key)).map(box)}</ol>
        <h4>Traits with their own settings</h4>
        {v.traits.length === 0
          ? <p className="sc-mut">None. Every trait is scored with the settings above.</p>
          : <table className="tbl">
              <thead><tr><th>Trait</th><th>C · Evidence model</th><th>E · Judge model</th>
                <th>When absent</th></tr></thead>
              <tbody>{v.traits.map((t) => (
                <tr key={t.trait_id}>
                  <td>{t.label ?? t.trait_id}</td>
                  {["evidence", judge].map((st) => (
                    <td key={st} className="mono">
                      {t.own_models.includes(st) ? <span className="sc-own">{t.models[st]}</span> : t.models[st]}
                    </td>))}
                  <td>{t.own_absence ? <span className="sc-own">{t.absence}</span> : t.absence}</td>
                </tr>))}</tbody>
            </table>}
        <p className="sc-mut">Highlighted: set by that trait, in place of the rater's own.</p>
      </div>
    </section>
  );
}

interface FitGateRow { fit_gate_id: string; name: string; version: number; model_id: string;
                      effort: string | null; template_id: string; prompt: string; note: string | null;
                      raters: number }
interface ScorerRow { scorer_id: string; name: string; version: number; level_method: string;
                      level_threshold: number; evidence_model: string; evidence_effort: string | null;
                      evidence_template_id: string; evidence_prompt: string; judge_model: string;
                      judge_effort: string | null; judge_template_id: string; judge_prompt: string;
                      normalization_version: string; context: unknown; absence: string | null;
                      trait_settings: unknown; note: string | null; raters: number }
interface TemplateRow { template_id: string; stage: string; version: string; status: string }

const EFFORTS = ["low", "medium", "high"];
const partName = (p: { name: string; version: number }) => `${p.name} v${p.version}`;

// A draft rater from a fit gate and a scorer: a new rater, the next version of one, or (for a
// draft that has not scored) the same draft with other parts.
function RaterForm({ rater, change = false, onCancel, onDone }: {
  rater?: RaterView["rater"]; change?: boolean; onCancel: () => void; onDone: (id?: string) => void;
}) {
  const gates = useGet<{ available: boolean; fit_gates: FitGateRow[] }>("/scoring/fit-gates");
  const scorers = useGet<{ available: boolean; scorers: ScorerRow[] }>("/scoring/scorers");
  const [key, setKey] = useState(rater?.config_key ?? "");
  const [gate, setGate] = useState(rater?.fit_gate_id ?? "");
  const [scorer, setScorer] = useState(rater?.scorer_id ?? "");
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    setBusy(true); setError(null);
    try {
      if (change && rater) {
        await api.put(`/scoring/raters/${encodeURIComponent(rater.config_id)}`,
                      { fit_gate_id: gate, scorer_id: scorer, rationale: why || null });
        onDone(rater.config_id);
      } else {
        const out = await api.post<{ config_id: string }>("/scoring/raters",
          { config_key: key, fit_gate_id: gate, scorer_id: scorer, rationale: why || null,
            from: rater?.config_id });
        onDone(out.config_id);
      }
    } catch (e) { setError(said(e)); } finally { setBusy(false); }
  }
  return (
    <AddForm saveLabel={change ? "Save the draft" : "Save as a draft"} busy={busy}
             missing={!key.trim() ? "Enter the rater key." : !gate ? "Choose a fit gate." : !scorer ? "Choose a scorer." : null} onSave={save} onCancel={onCancel}
             error={error} note="A draft scores only in experiments until it is promoted.">
      {!change && <label>Rater key<input value={key} onChange={(e) => setKey(e.target.value)}
                                         placeholder="writing-default" disabled={!!rater} /></label>}
      <label>B · Fit gate
        <select value={gate} onChange={(e) => setGate(e.target.value)}>
          <option value="">Choose a fit gate</option>
          {(gates.data?.fit_gates ?? []).map((g) => <option key={g.fit_gate_id} value={g.fit_gate_id}>
            {partName(g)} · {g.model_id} · {g.effort ?? "default"} effort</option>)}
        </select></label>
      <label>C–F · Scorer
        <select value={scorer} onChange={(e) => setScorer(e.target.value)}>
          <option value="">Choose a scorer</option>
          {(scorers.data?.scorers ?? []).map((s) => <option key={s.scorer_id} value={s.scorer_id}>
            {partName(s)} · {methodName(s.level_method)}</option>)}
        </select></label>
      <label>Why<input value={why} onChange={(e) => setWhy(e.target.value)}
                       placeholder="A cheaper fit gate" /></label>
    </AddForm>
  );
}

function useApproved(): string[] {
  const models = useGet<{ available: boolean; models: ModelRow[] }>("/scoring/models");
  return (models.data?.models ?? []).filter((m) => m.status === "approved" || m.status === "candidate")
    .map((m) => m.model_id);
}

function Pick({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void; options: [string, string][];
}) {
  return (
    <label>{label}
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose</option>
        {options.map(([v, text]) => <option key={v} value={v}>{text}</option>)}
      </select></label>
  );
}

export function ScoringFitGates() {
  const [reload, setReload] = useState(0);
  const load = useGet<{ available: boolean; fit_gates: FitGateRow[] }>(`/scoring/fit-gates?r=${reload}`);
  const templates = useGet<{ available: boolean; templates: TemplateRow[] }>("/scoring/templates");
  const models = useApproved();
  const can = useCan();
  const [from, setFrom] = useState<FitGateRow | "new" | null>(null);
  const [f, setF] = useState({ name: "", model_id: "", effort: "", template_id: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const status = notReady(load, "fit gates");
  function open(g: FitGateRow | "new") {
    setFrom(g); setError(null);
    setF(g === "new" ? { name: "", model_id: "", effort: "low", template_id: "" }
                     : { name: g.name, model_id: g.model_id, effort: g.effort ?? "", template_id: g.template_id });
  }
  async function save() {
    setBusy(true); setError(null);
    try { await api.post("/scoring/fit-gates", f); setFrom(null); setReload((n) => n + 1); }
    catch (e) { setError(said(e)); } finally { setBusy(false); }
  }
  return (
    <div className="gl sc">
      <h2>Fit gates</h2>
      <p className="gl-lede">Stage B: is this paper an attempt at the task? A fit gate never changes once saved; a change is a new version, and a rater picks one.</p>
      <div className="sc-head"><button type="button" className="btn sm" disabled={!can.maintain}
        title={can.maintain ? undefined : NOT_MAINTAINER} onClick={() => open("new")}>New fit gate</button></div>
      {from && (
        <AddForm saveLabel="Save" busy={busy} missing={!f.name.trim() ? "Enter a name." : !f.model_id ? "Choose a model." : !f.template_id ? "Choose a prompt." : null}
                 onSave={save} onCancel={() => setFrom(null)} error={error}
                 note={from === "new" ? "A new name starts at version 1." : `Saved as the next version of ${from.name}.`}>
          <label>Name<input value={f.name} disabled={from !== "new"}
                            onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="task-fit" /></label>
          <Pick label="Model" value={f.model_id} onChange={(v) => setF({ ...f, model_id: v })}
                options={models.map((m) => [m, m])} />
          <Pick label="Effort" value={f.effort} onChange={(v) => setF({ ...f, effort: v })}
                options={EFFORTS.map((e) => [e, e])} />
          <Pick label="Prompt" value={f.template_id} onChange={(v) => setF({ ...f, template_id: v })}
                options={(templates.data?.templates ?? []).filter((t) => t.stage === "fit")
                  .map((t) => [t.template_id, `${t.version} · ${t.status}`])} />
        </AddForm>)}
      {status ?? (load.data!.fit_gates.length === 0 ? <p className="sc-mut">None yet.</p> : (
        <table className="tbl">
          <thead><tr><th>Fit gate</th><th>Model</th><th>Effort</th><th>Prompt</th><th>Raters</th><th /></tr></thead>
          <tbody>{load.data!.fit_gates.map((g) => (
            <tr key={g.fit_gate_id}><td>{partName(g)}</td><td className="mono">{g.model_id}</td>
              <td>{g.effort ?? <span className="sc-mut">default</span>}</td><td>{g.prompt}</td>
              <td>{g.raters}</td>
              <td><button type="button" className="btn-link" disabled={!can.maintain}
                          onClick={() => open(g)}>New version</button></td></tr>))}</tbody>
        </table>))}
    </div>
  );
}

export function ScoringScorers() {
  const [reload, setReload] = useState(0);
  const load = useGet<{ available: boolean; scorers: ScorerRow[] }>(`/scoring/scorers?r=${reload}`);
  const templates = useGet<{ available: boolean; templates: TemplateRow[] }>("/scoring/templates");
  const models = useApproved();
  const can = useCan();
  const blank = { name: "", level_method: "level_ladder", level_threshold: 0.5, evidence_model: "",
                  evidence_effort: "high", evidence_template_id: "", judge_model: "",
                  judge_effort: "high", judge_template_id: "", absence: "abstain" };
  const [from, setFrom] = useState<ScorerRow | "new" | null>(null);
  const [f, setF] = useState<typeof blank & { context?: unknown; trait_settings?: unknown;
                               normalization_version?: string }>(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const status = notReady(load, "scorers");
  const prompts = (stage: string): [string, string][] => (templates.data?.templates ?? [])
    .filter((t) => t.stage === stage).map((t) => [t.template_id, `${t.version} · ${t.status}`]);
  const judgeStage = judgeOf(f.level_method);
  function open(s: ScorerRow | "new") {
    setFrom(s); setError(null);
    setF(s === "new" ? blank : {
      name: s.name, level_method: s.level_method, level_threshold: s.level_threshold,
      evidence_model: s.evidence_model, evidence_effort: s.evidence_effort ?? "",
      evidence_template_id: s.evidence_template_id, judge_model: s.judge_model,
      judge_effort: s.judge_effort ?? "", judge_template_id: s.judge_template_id,
      absence: s.absence ?? "abstain",
      // Carried from the version it is made from; set per trait on the rater's trait table.
      context: s.context, trait_settings: s.trait_settings, normalization_version: s.normalization_version });
  }
  async function save() {
    setBusy(true); setError(null);
    try { await api.post("/scoring/scorers", f); setFrom(null); setReload((n) => n + 1); }
    catch (e) { setError(said(e)); } finally { setBusy(false); }
  }
  const missing = !f.name.trim() ? "Enter a name." : !f.evidence_model ? "Choose the evidence model."
    : !f.evidence_template_id ? "Choose the evidence prompt." : !f.judge_model ? "Choose the judge model."
    : !f.judge_template_id ? "Choose the judge prompt." : null;
  return (
    <div className="gl sc">
      <h2>Scorers</h2>
      <p className="gl-lede">Stages C–F: find the evidence, verify it, judge the level, combine. A scorer never changes once saved; a change is a new version, and a rater picks one.</p>
      <div className="sc-head"><button type="button" className="btn sm" disabled={!can.maintain}
        title={can.maintain ? undefined : NOT_MAINTAINER} onClick={() => open("new")}>New scorer</button></div>
      {from && (
        <AddForm saveLabel="Save" busy={busy} missing={missing} onSave={save} onCancel={() => setFrom(null)}
                 error={error}
                 note={from === "new" ? "A new name starts at version 1." : `Saved as the next version of ${from.name}.`}>
          <label>Name<input value={f.name} disabled={from !== "new"}
                            onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="level-ladder" /></label>
          <Pick label="How levels are decided" value={f.level_method}
                onChange={(v) => setF({ ...f, level_method: v, judge_template_id: "" })}
                options={[["one_level_call", "one level call"], ["level_ladder", "level ladder"],
                          ["pairwise_anchors", "pairwise against anchors"]]} />
          {f.level_method === "level_ladder" && (
            <label>Ladder threshold<input type="number" min={0.05} max={0.95} step={0.05}
              value={f.level_threshold} onChange={(e) => setF({ ...f, level_threshold: Number(e.target.value) })} /></label>)}
          <Pick label="C · Evidence model" value={f.evidence_model}
                onChange={(v) => setF({ ...f, evidence_model: v })} options={models.map((m) => [m, m])} />
          <Pick label="C · Evidence effort" value={f.evidence_effort}
                onChange={(v) => setF({ ...f, evidence_effort: v })} options={EFFORTS.map((e) => [e, e])} />
          <Pick label="C · Evidence prompt" value={f.evidence_template_id}
                onChange={(v) => setF({ ...f, evidence_template_id: v })} options={prompts("evidence")} />
          <Pick label="E · Judge model" value={f.judge_model}
                onChange={(v) => setF({ ...f, judge_model: v })} options={models.map((m) => [m, m])} />
          <Pick label="E · Judge effort" value={f.judge_effort}
                onChange={(v) => setF({ ...f, judge_effort: v })} options={EFFORTS.map((e) => [e, e])} />
          <Pick label="E · Judge prompt" value={f.judge_template_id}
                onChange={(v) => setF({ ...f, judge_template_id: v })} options={prompts(judgeStage)} />
          <Pick label="When a trait is absent" value={f.absence} onChange={(v) => setF({ ...f, absence: v })}
                options={[["abstain", "abstain"], ["lowest", "score the lowest level, flagged"]]} />
          {f.level_method === "pairwise_anchors" && (
            <p className="sc-mut sc-wide">Starts at the top level and steps down until the paper beats
              1 of a level's anchors. The anchors are chosen by each experiment, from its prompt.</p>)}
        </AddForm>)}
      {status ?? (load.data!.scorers.length === 0 ? <p className="sc-mut">None yet.</p> : (
        <table className="tbl">
          <thead><tr><th>Scorer</th><th>Method</th><th>C · Evidence</th><th>E · Judge</th>
            <th>When absent</th><th>Raters</th><th /></tr></thead>
          <tbody>{load.data!.scorers.map((s) => (
            <tr key={s.scorer_id}><td>{partName(s)}</td>
              <td>{methodName(s.level_method)}{s.level_method === "level_ladder" ? ` · ${s.level_threshold}`
                   : ""}</td>
              <td className="mono">{s.evidence_model} · {s.evidence_effort ?? "default"} · {s.evidence_prompt}</td>
              <td className="mono">{s.judge_model} · {s.judge_effort ?? "default"} · {s.judge_prompt}</td>
              <td>{s.absence === "lowest" ? "lowest level, flagged" : "abstain"}</td><td>{s.raters}</td>
              <td><button type="button" className="btn-link" disabled={!can.maintain}
                          onClick={() => open(s)}>New version</button></td></tr>))}</tbody>
        </table>))}
    </div>
  );
}

interface ModelRow { model_id: string; provider: string; status: string; approved_by: string | null;
                     retires_on: string | null; raters: number }

export function ScoringModels() {
  const load = useGet<{ available: boolean; models: ModelRow[] }>("/scoring/models");
  const status = notReady(load, "models");
  return (
    <div className="gl sc">
      <h2>Models</h2>
      <p className="gl-lede">Which models may score. A deprecated model keeps running for classes already using it, until it retires.</p>
      {status ?? (load.data!.models.length === 0 ? <p className="sc-mut">None yet.</p> : (
        <table className="tbl">
          <thead><tr><th>Model</th><th>Status</th><th>Retires</th><th>Raters naming it</th><th>Approved by</th></tr></thead>
          <tbody>{load.data!.models.map((m) => (
            <tr key={m.model_id}><td className="mono">{m.model_id}</td><td>{m.status}</td>
              <td>{m.retires_on ?? <span className="sc-mut">not announced</span>}</td>
              <td>{m.raters}</td><td>{m.approved_by ?? "—"}</td></tr>))}</tbody>
        </table>))}
    </div>
  );
}

// ---------------------------------------------------------------------------- Sets, Experiments

// ---------------------------------------------------------------------------- who may do what

export interface Can { view: boolean; maintain: boolean; lead: boolean }

/** What the caller may do here. A UI hint: every write asks again, and the database refuses the
 *  rest (0098). A button the caller may not use stays, disabled, saying why. */
function useCan(): Can {
  const load = useGet<Can>("/scoring/can");
  return load.data ?? { view: true, maintain: false, lead: false };
}

const NOT_MAINTAINER = "Building sets and running experiments is for scoring maintainers and leads.";
const NOT_LEAD = "Only a scoring lead can do this.";

function said(e: unknown): string {
  return e instanceof ApiError ? (e.detail ?? e.message) : "That did not work. Try again.";
}

// ------------------------------------------------------------------------------ Sets

interface SetRow { set_id: string; prompt: string; curricula?: string[]; purpose: string;
                   version: number; name: string; frozen_at: string | null;
                   frozen_by: string | null; created_at?: string; papers: number;
                   one_human: number; double_scored_traits: number }
interface PromptRow { task_id: string; name: string; curricula?: string[]; papers: number;
                      with_human: number; in_sets: number }

/** Every curriculum the rows are taught in, A to Z. Pure. */
export function curriculaOf(rows: { curricula?: string[] }[]): string[] {
  return [...new Set(rows.flatMap((r) => r.curricula ?? []))].sort((a, b) => a.localeCompare(b));
}

/** Whether a row is in the chosen curriculum; "" is every curriculum. Pure. */
export const inCurriculum = (row: { curricula?: string[] }, curriculum: string) =>
  !curriculum || (row.curricula ?? []).includes(curriculum);

export const PURPOSES = [
  { key: "canary", label: "Canary", hint: "about 20 papers, run first to catch a broken rater" },
  { key: "development", label: "Development", hint: "about 60 papers, to try changes on" },
  { key: "locked", label: "Locked", hint: "the papers a rater is finally judged on; a lead makes it" },
  { key: "anchors", label: "Anchors", hint: "papers at each human level, for a pairwise scorer to compare with" },
] as const;

/** The sets a new one may start from: older versions of the same prompt and purpose. Pure. */
export function startFromOptions(sets: { set_id: string; prompt: string; purpose: string;
                                         version: number; papers: number }[],
                                 prompt: string | undefined, purpose: string): [string, string][] {
  return sets.filter((x) => x.prompt === prompt && x.purpose === purpose)
    .sort((a, b) => b.version - a.version)
    .map((x) => [x.set_id, `${x.purpose} v${x.version} · ${x.papers} papers`]);
}

/** The anchor sets an experiment on `set` may compare with: its prompt's, newest first. Pure. */
export function anchorSetsFor(sets: { set_id: string; prompt: string; purpose: string;
                                      version: number; papers: number }[],
                              set: { prompt: string } | undefined): [string, string][] {
  if (!set) return [];
  return sets.filter((x) => x.purpose === "anchors" && x.prompt === set.prompt)
    .sort((a, b) => b.version - a.version)
    .map((x) => [x.set_id, `anchors v${x.version} · ${x.papers} papers`]);
}

/** Why the set cannot be made yet, or null. Pure. */
export function makeSetWhy(purpose: string, can: Can): string | null {
  if (!can.maintain) return NOT_MAINTAINER;
  if (purpose === "locked" && !can.lead) return "A locked set is made by a scoring lead.";
  return null;
}

export function ScoringSets() {
  const [reload, setReload] = useState(0);
  const load = useGet<{ available: boolean; sets: SetRow[] }>(`/scoring/sets?r=${reload}`);
  const can = useCan();
  const [adding, setAdding] = useState(false);
  const [made, setMade] = useState<string | null>(null);
  const [curriculum, setCurriculum] = useState("");
  const status = notReady(load, "evaluation sets");
  const all = load.data?.sets ?? [];
  const curricula = curriculaOf(all);
  const shown = all.filter((s) => inCurriculum(s, curriculum));

  return (
    <div className="gl sc">
      <SectionHead title="Evaluation sets" level={2} addLabel="New evaluation set" adding={adding}
                   onAdd={() => { setAdding(!adding); setMade(null); }}
                   disabledWhy={can.maintain ? null : NOT_MAINTAINER} />
      <p className="gl-lede">Papers a rater is tried on, per prompt: canary, development and locked — and anchor sets, the papers a pairwise scorer compares with. A set's papers are fixed when it is made; to change them, make a new version. Anchor and locked papers are never in another set.</p>
      {curricula.length > 0 && (
        <label className="sc-filter">Curriculum{" "}
          <select value={curriculum} onChange={(e) => setCurriculum(e.target.value)}>
            <option value="">All curricula</option>
            {curricula.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>)}
      {adding && <NewSet sets={all} can={can} curriculum={curriculum} onCancel={() => setAdding(false)}
                         onMade={(text) => { setAdding(false); setMade(text); setReload((n) => n + 1); }} />}
      {made && <p className="sc-note">{made}</p>}
      {status ?? (shown.length === 0 ? <p className="sc-mut">None yet.</p> : (
        <table className="tbl">
          <thead><tr><th>Prompt</th><th>Purpose</th><th>Name</th><th>Papers</th>
            <th title="Papers in the set with at least one human trait score">Papers with a human score</th>
            <th title="Trait scores given by two different people: what human–human agreement needs">Trait scores by two people</th>
            <th>Created</th></tr></thead>
          <tbody>{shown.map((s) => (
            <tr key={s.set_id}>
              <td title={(s.curricula ?? []).length ? `Curriculum: ${(s.curricula ?? []).join(", ")}` : undefined}>
                {s.prompt}</td>
              <td>{s.purpose} v{s.version}</td>
              <td title={s.set_id}>{s.name}</td>
              <td>{s.papers}</td><td>{s.one_human}</td><td>{s.double_scored_traits}</td>
              <td title={s.frozen_by ? `Made by ${s.frozen_by}` : undefined}>
                {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}</td></tr>))}</tbody>
        </table>))}
    </div>
  );
}

function NewSet({ sets, can, curriculum: start, onCancel, onMade }: {
  sets: SetRow[]; can: Can; curriculum: string; onCancel: () => void;
  onMade: (said: string) => void }) {
  const prompts = useGet<{ available: boolean; prompts: PromptRow[] }>("/scoring/prompts");
  const [curriculum, setCurriculum] = useState(start);
  const [task, setTask] = useState("");
  const [purpose, setPurpose] = useState<string>("canary");
  const [size, setSize] = useState(20);
  const [name, setName] = useState("");
  const [from, setFrom] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const every = prompts.data?.prompts ?? [];
  const curricula = curriculaOf(every);
  const list = every.filter((p) => inCurriculum(p, curriculum));
  const chosen = list.find((p) => p.task_id === task);
  const purposeLabel = PURPOSES.find((p) => p.key === purpose)?.label ?? purpose;
  const shownName = name || (chosen ? `${chosen.name} · ${purposeLabel.toLowerCase()}` : "");
  const free = chosen ? chosen.papers - chosen.in_sets : 0;
  const anchors = purpose === "anchors";
  const starts = startFromOptions(sets, chosen?.name, purpose);
  const why = makeSetWhy(purpose, can);

  async function save() {
    setBusy(true); setError(null);
    try {
      const out = await api.post<{ set_id: string; added: number; short: number }>(
        "/scoring/sets", { task_id: task, purpose, size, name: shownName, start_from: from || null });
      onMade(`Made ${out.set_id} with ${out.added} papers`
        + (anchors ? ` — up to ${size} at each level people scored.` : out.short > 0 ? `; ${out.short} fewer than asked, because no more reference papers of this prompt are outside its other sets.` : "."));
    } catch (e) { setError(said(e)); } finally { setBusy(false); }
  }

  return (
    <AddForm saveLabel="Make the set" busy={busy} missing={!chosen ? "Choose a prompt." : size <= 0 ? "Ask for at least one paper." : why}
             onSave={save} onCancel={onCancel} error={error ?? why}
             note={!chosen ? "A set is one prompt's reference papers. Paper text is never shown here."
               : anchors ? `${chosen.with_human} of its ${chosen.papers} reference papers have a human `
               + "score. An anchor set takes that many papers at each level people scored, in a "
               + "fixed order, and a paper in an anchor set is never in another set of this prompt."
               : `${free} of its ${chosen.papers} reference papers are in no set yet; `
               + `${chosen.with_human} have a human score. Papers with a human score are taken `
               + "first, then the rest in a fixed order, so nobody picks papers by reading them."}>
      {curricula.length > 0 && (
        <label>
          Curriculum
          <select value={curriculum} onChange={(e) => { setCurriculum(e.target.value); setTask(""); setFrom(""); }}>
            <option value="">All curricula</option>
            {curricula.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>)}
      <label>
        Prompt
        <select value={task} onChange={(e) => { setTask(e.target.value); setFrom(""); }}>
          <option value="">{prompts.data ? "Choose a prompt" : "loading prompts…"}</option>
          {list.map((p) => <option key={p.task_id} value={p.task_id}>
            {p.name} · {p.papers} papers</option>)}
        </select>
      </label>
      <label>
        Purpose
        <select value={purpose} onChange={(e) => {
          setPurpose(e.target.value); setFrom(""); if (e.target.value === "anchors") setSize(3); }}>
          {PURPOSES.map((p) => <option key={p.key} value={p.key}>{p.label}: {p.hint}</option>)}
        </select>
      </label>
      {starts.length > 0 && (
        <label>
          Start from
          <select value={from} onChange={(e) => setFrom(e.target.value)}>
            <option value="">nothing: new papers only</option>
            {starts.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </label>)}
      <label>
        {anchors ? "Papers per level" : from ? "Papers in all" : "Papers"}
        <input type="number" min={1} max={2000} value={size}
               onChange={(e) => setSize(Number(e.target.value))} />
      </label>
      <label>
        Name
        <input value={shownName} onChange={(e) => setName(e.target.value)} />
      </label>
    </AddForm>
  );
}

// ------------------------------------------------------------------------------ Experiments

interface ExperimentRow { experiment_id: string; set_id: string; set_name: string;
                          set_purpose: string; set_papers: number; rubric_id: string | null;
                          rubric_name: string | null; config_key: string;
                          rater_version: number; level_method: string; scores_as_of: string;
                          requested_by: string; metrics: number; created_at: string;
                          anchor_set_id?: string | null }

/** How a set reads in a list: its name, purpose and size. Pure. */
export function setLine(x: { set_name: string; set_purpose: string; set_papers: number }): string {
  return `${x.set_name} · ${x.set_purpose} · ${x.set_papers} papers`;
}

export function ScoringExperiments() {
  const [reload, setReload] = useState(0);
  const list = useGet<{ available: boolean; experiments: ExperimentRow[] }>(`/scoring/experiments?r=${reload}`);
  const can = useCan();
  const [adding, setAdding] = useState(false);
  const [chosen, choose] = useQueryParam("experiment");
  const status = notReady(list, "experiments");
  const current = list.data?.experiments.find((x) => x.experiment_id === chosen);
  return (
    <div className="gl sc">
      <SectionHead title="Experiments" level={2} addLabel="New experiment" adding={adding}
                   onAdd={() => setAdding(!adding)}
                   disabledWhy={can.maintain ? null : NOT_MAINTAINER} />
      <p className="gl-lede">One rater tried on one evaluation set, reading human scores as of one moment.</p>
      {adding && <NewExperiment onCancel={() => setAdding(false)}
                                onMade={(id) => { setAdding(false); setReload((n) => n + 1); choose(id); }} />}
      {status ?? (list.data!.experiments.length === 0 ? <p className="sc-mut">None yet.</p> : (
        <table className="tbl">
          <thead><tr><th>Experiment</th><th>Rater</th><th>Set</th><th>Rubric</th>
            <th>Scorecard</th></tr></thead>
          <tbody>{list.data!.experiments.map((x) => (
            <tr key={x.experiment_id} className={x.experiment_id === chosen ? "cur" : undefined}>
              <td><button type="button" className="btn-link" onClick={() => choose(x.experiment_id)}>
                {x.experiment_id}</button></td>
              <td>{raterName({ config_key: x.config_key, version: x.rater_version })}</td>
              <td title={x.set_id}>{setLine(x)}</td>
              <td>{x.rubric_name ?? <span className="sc-mut">none recorded</span>}</td>
              <td>{x.metrics ? "recorded" : <span className="sc-mut">not recorded</span>}</td></tr>))}</tbody>
        </table>))}
      {chosen && current && (
        <Experiment key={`${chosen}:${reload}`} exp={current} can={can}
                    onRecorded={() => setReload((n) => n + 1)} />)}
    </div>
  );
}

function NewExperiment({ onCancel, onMade }: { onCancel: () => void; onMade: (id: string) => void }) {
  const sets = useGet<{ available: boolean; sets: SetRow[] }>("/scoring/sets");
  const raters = useGet<{ available: boolean; raters: Rater[] }>("/scoring/raters");
  const frozen = sets.data?.sets ?? [];
  const [setId, setSetId] = useState("");
  const [config, setConfig] = useState("");
  const [rubric, setRubric] = useState("");
  const [anchors, setAnchors] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rater = (raters.data?.raters ?? []).find((r) => r.config_id === config);
  const pairwise = rater?.level_method === "pairwise_anchors";
  const chosenSet = frozen.find((s) => s.set_id === setId);
  const anchorSets = anchorSetsFor(frozen, chosenSet);
  const rubrics = useGet<{ available: boolean; rubrics: { rubric_id: string; name: string;
                                                         traits: number }[] }>(
    setId ? `/scoring/sets/${encodeURIComponent(setId)}/rubrics` : null);

  async function save() {
    setBusy(true); setError(null);
    try {
      const out = await api.post<{ experiment_id: string }>("/scoring/experiments",
                                                            { set_id: setId, config_id: config,
                                                              rubric_id: rubric,
                                                              anchor_set_id: pairwise ? anchors : null });
      onMade(out.experiment_id);
    } catch (e) { setError(said(e)); } finally { setBusy(false); }
  }

  return (
    <AddForm saveLabel="Record the experiment" busy={busy}
             missing={!setId ? "Choose a set." : !config ? "Choose a rater." : !rubric ? "Choose a rubric."
                      : pairwise && !anchors ? "Choose the anchors." : null}
             onSave={save} onCancel={onCancel} error={error}
             note={frozen.length === 0 && sets.data ? "No set yet. Make one on Evaluation sets first."
               : rater && rater.level_method !== "level_ladder"
                 ? `This rater is a ${methodName(rater.level_method)} rater, so stage F cannot be replayed on it. A level ladder rater can be.`
                 : "Recording it scores nothing. Running it is the next step, on its panel."}>
      <label>
        Set
        <select value={setId} onChange={(e) => { setSetId(e.target.value); setRubric(""); setAnchors(""); }}>
          <option value="">Choose a set</option>
          {frozen.map((s) => <option key={s.set_id} value={s.set_id}>{s.name} · {s.papers} papers</option>)}
        </select>
      </label>
      <label>
        Rater
        <select value={config} onChange={(e) => setConfig(e.target.value)}>
          <option value="">Choose a rater</option>
          {(raters.data?.raters ?? []).map((r) => <option key={r.config_id} value={r.config_id}>
            {raterName(r)} · {r.status} · {methodName(r.level_method)}</option>)}
        </select>
      </label>
      <label>
        Rubric
        <select value={rubric} disabled={!setId} onChange={(e) => setRubric(e.target.value)}>
          <option value="">{setId ? "Choose the rubric it measures" : "Choose a set first"}</option>
          {(rubrics.data?.rubrics ?? []).map((r) => <option key={r.rubric_id} value={r.rubric_id}>
            {r.name} · {r.traits} {r.traits === 1 ? "trait" : "traits"}</option>)}
        </select>
      </label>
      {pairwise && (
        <label>
          Anchors
          <select value={anchors} disabled={!setId} onChange={(e) => setAnchors(e.target.value)}>
            <option value="">{!setId ? "Choose a set first" : anchorSets.length
              ? "Choose the anchor set" : "No anchor set for this prompt yet"}</option>
            {anchorSets.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </label>)}
    </AddForm>
  );
}

interface Progress { available?: boolean; papers: number; copies: number; waiting: number; reached: number;
                     not_scorable: number; done: boolean; running: boolean }
interface Preview extends Omit<Progress, "running"> { to_copy: number; refused: string[];
                                                     rater: string; started: boolean }

/** The line under the progress bar. Pure. */
export function progressLine(p: Progress): string {
  if (p.copies === 0) return p.running ? "Starting." : "Not run yet.";
  const parts = [`${p.reached} of ${p.papers} papers scored`];
  if (p.not_scorable) parts.push(`${p.not_scorable} not scorable (a non-attempt, never a low score)`);
  if (p.done) parts.push("finished");
  else if (p.running) parts.push("running");
  else if (p.waiting) parts.push(`${p.waiting} waiting, and no run is going`);
  return parts.join(" · ");
}

function Experiment({ exp, can, onRecorded }: { exp: ExperimentRow; can: Can; onRecorded: () => void }) {
  const id = encodeURIComponent(exp.experiment_id);
  const [tick, setTick] = useState(0);
  const progress = useGet<Progress>(`/scoring/experiments/${id}/progress?t=${tick}`);
  const p = progress.data;
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // While a run is going, look again every ten seconds. Counts over the copies, never a stored
  // status: what the bar says is what the database holds.
  useEffect(() => {
    if (!p?.running) return;
    const t = window.setTimeout(() => setTick((n) => n + 1), 10000);
    return () => window.clearTimeout(t);
  }, [p]);

  async function ask() {
    setBusy(true); setError(null);
    try { setPreview(await api.post<Preview>(`/scoring/experiments/${id}/run`, { start: false })); }
    catch (e) { setError(said(e)); } finally { setBusy(false); }
  }
  async function start() {
    setBusy(true); setError(null);
    try {
      await api.post(`/scoring/experiments/${id}/run`, { start: true });
      setPreview(null); setTick((n) => n + 1);
    } catch (e) { setError(said(e)); setPreview(null); } finally { setBusy(false); }
  }

  const runWhy = !can.maintain ? NOT_MAINTAINER : p?.done ? "Every paper is already scored."
    : p?.running ? "A run is going now." : null;
  const toScore = preview ? preview.papers - preview.reached : 0;
  return (
    <section className="sc-card" aria-label={`Experiment ${exp.experiment_id}`}>
      <div className="sc-head">
        <h3 className="sc-title">{exp.experiment_id}</h3>
        <span className="sc-mut">{raterName({ config_key: exp.config_key, version: exp.rater_version })}
          {" · "}{exp.rubric_name ?? "no rubric recorded"} · {setLine(exp)}</span>
      </div>
      <details className="sc-more">
        <summary>More</summary>
        <dl className="sc-kv">
          <dt>Experiment</dt><dd className="mono">{exp.experiment_id}</dd>
          <dt>Set</dt><dd className="mono">{exp.set_id}</dd>
          <dt>Rater's judge</dt><dd>{exp.level_method}</dd>
          {exp.anchor_set_id && <><dt>Anchors</dt><dd className="mono">{exp.anchor_set_id}</dd></>}
          <dt>Recorded by</dt><dd>{exp.requested_by}</dd>
          <dt>Human scores read up to</dt><dd>{new Date(exp.scores_as_of).toLocaleString()}</dd>
        </dl>
      </details>
      <h4>Run</h4>
      {notReady(progress, "progress") ?? (
        <div className="sc-run">
          <progress max={Math.max(p!.papers, 1)} value={p!.reached} aria-label="Papers scored" />
          <span>{progressLine(p!)}</span>
          <button type="button" className="btn sm primary" disabled={busy || !!runWhy}
                  title={runWhy ?? undefined} onClick={ask}>
            {p!.copies && !p!.done ? "Continue the run…" : "Run…"}</button>
        </div>)}
      {error && <p className="inc-error">{error}</p>}
      <ConfirmDialog open={!!preview} danger={false} busy={busy} confirmLabel="Start the run"
                     title={`Score ${toScore} papers with ${preview?.rater ?? ""}?`}
                     onConfirm={start} onCancel={() => setPreview(null)}>
        {preview && preview.refused.length > 0 ? (
          <><p>This set cannot be run:</p><ul>{preview.refused.map((r) => <li key={r}>{r}</li>)}</ul></>
        ) : (
          <p>Each paper is scored by the model, trait by trait, the way production scores: this
            costs money. {preview && preview.reached > 0
              ? `${preview.reached} already scored are not scored again. ` : ""}
            The run happens in the scoring job and takes a while; this panel counts as it goes.</p>
        )}
      </ConfirmDialog>
      <ExperimentScorecard exp={exp} can={can} done={!!p?.done} scored={p?.reached ?? 0}
                           onRecorded={onRecorded} />
    </section>
  );
}

// ------------------------------------------------------------------------------ Scorecard

const COLUMNS: { metric: string; label: string }[] = [
  { metric: "qwk", label: "QWK" }, { metric: "qwk_human_human", label: "QWK human–human" },
  { metric: "degradation", label: "Δ" }, { metric: "smd", label: "SMD" },
  { metric: "exact", label: "Exact" }, { metric: "levels_used", label: "Levels used" },
  { metric: "not_present_rate", label: "Not present" },
  { metric: "abstention_rate", label: "Unable to score" },
];

export const STAGE_F = [
  { key: "as-run", label: "As run" },
  { key: "ladder", label: "Ladder" },
  { key: "expected", label: "Expected" },
  { key: "most_likely", label: "Most likely" },
] as const;
type StageF = typeof STAGE_F[number]["key"];

interface Card { metrics: Metric[]; policy: Policy[]; recorded: boolean;
                 replay: { method: string; threshold: number | null; changed: number } | null }

function ExperimentScorecard({ exp, can, done, scored, onRecorded }: {
  exp: ExperimentRow; can: Can; done: boolean; scored: number; onRecorded: () => void;
}) {
  const id = encodeURIComponent(exp.experiment_id);
  const stored = useGet<{ available: boolean; metrics: Metric[]; policy: Policy[];
                          issues?: Issue[]; papers?: number }>(`/scoring/experiments/${id}`);
  const [method, setMethod] = useState<StageF>("as-run");
  const [threshold, setThreshold] = useState(0.5);
  const [card, setCard] = useState<Card | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const recorded = (stored.data?.metrics.length ?? 0) > 0;
  const ladder = exp.level_method === "level_ladder";

  // Computed now whenever there is nothing recorded to show, or a replay is asked for; again as
  // a run scores more papers, so what is shown is never older than the progress bar.
  useEffect(() => {
    if (!stored.data || (recorded && method === "as-run")) { setCard(null); return; }
    let live = true;
    setBusy(true); setError(null);
    (async () => {
      const replay = method === "as-run" ? undefined
        : await api.get<unknown>(`/scoring/experiments/${id}/replay?method=${method}&threshold=${threshold}`);
      return api.post<Card>(`/scoring/experiments/${id}/scorecard`, replay ? { replay } : {});
    })().then((c) => { if (live) setCard(c); }, (e) => { if (live) setError(said(e)); })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [stored.data, recorded, method, threshold, id, scored]);

  async function record() {
    setBusy(true); setError(null);
    try {
      await api.post(`/scoring/experiments/${id}/scorecard`, { record: true });
      setRecording(false); onRecorded();
    } catch (e) { setError(said(e)); setRecording(false); } finally { setBusy(false); }
  }

  const status = notReady(stored, "scorecard");
  if (status) return status;
  const shown = recorded && method === "as-run"
    ? { metrics: stored.data!.metrics, policy: stored.data!.policy } : card;
  const recordWhy = !can.lead ? NOT_LEAD : recorded ? "Already recorded."
    : !done ? "Record it when every paper is scored." : null;
  return (
    <div>
      <div className="sc-head">
        <h4>Scorecard</h4>
        {ladder && (
          <span className="sc-seg-ctl" role="group" aria-label="Stage F">
            {STAGE_F.map((m) => (
              <button key={m.key} type="button" className={"btn sm" + (m.key === method ? " primary" : "")}
                      aria-pressed={m.key === method} onClick={() => setMethod(m.key)}>{m.label}</button>))}
            {method === "ladder" && (
              <label className="sc-mut">threshold{" "}
                <input type="number" min={0.05} max={0.95} step={0.05} value={threshold}
                       onChange={(e) => setThreshold(Number(e.target.value))} /></label>)}
          </span>)}
        {!recorded && method === "as-run" && (
          <button type="button" className="btn sm" disabled={busy || !!recordWhy}
                  title={recordWhy ?? undefined} onClick={() => setRecording(true)}>Record the scorecard…</button>)}
      </div>
      <p className="sc-mut">{
        method !== "as-run"
          ? `Replay: stage F by ${method.replace("_", " ")}${method === "ladder" ? ` at ${threshold}` : ""}`
            + (card?.replay ? ` · ${card.replay.changed} levels changed` : "")
            + " · computed now from the stored answers, never recorded, no model called."
          : recorded ? "Recorded: what this rater did on this set."
            : "Computed now from what is stored. Not recorded yet."}</p>
      {error && <p className="inc-error">{error}</p>}
      {!shown ? <p className="muted dots">computing</p>
        : <ScorecardTable metrics={shown.metrics} policy={shown.policy} />}
      <Issues issues={stored.data!.issues ?? []} papers={stored.data!.papers ?? null} />
      <ConfirmDialog open={recording} danger={false} busy={busy} confirmLabel="Record"
                     title="Record this scorecard?" onConfirm={record} onCancel={() => setRecording(false)}>
        <p>The rows are written once and never change: they become the record of how this rater
          did on this set, judged against the thresholds in force today.</p>
      </ConfirmDialog>
    </div>
  );
}

// What the rater flagged while scoring this set (0103), per trait: how many papers, and the sentence
// a teacher reads beside the score.
interface Issue { trait_id: string; trait_label: string | null; code: string; papers: number;
                  example: string | null }
const ISSUE_LABEL: Record<string, string> = { element_not_present: "Not present, scored lowest" };

export function Issues({ issues, papers }: { issues: Issue[]; papers: number | null }) {
  return (
    <div className="sc-issues">
      <h4>Issues</h4>
      {issues.length === 0
        ? <p className="sc-mut">Nothing flagged. A rater that abstains when a trait is absent flags
            nothing here; those papers count under Not present.</p>
        : <table className="tbl">
            <thead><tr><th>Trait</th><th>Issue</th><th>Papers</th><th>The teacher reads</th></tr></thead>
            <tbody>{issues.map((i) => (
              <tr key={i.trait_id + i.code}>
                <td>{i.trait_label ?? i.trait_id}</td>
                <td>{ISSUE_LABEL[i.code] ?? i.code.replace(/_/g, " ")}</td>
                <td>{i.papers}{papers ? ` of ${papers}` : ""}</td>
                <td className="sc-mut">{i.example}</td>
              </tr>))}</tbody>
          </table>}
    </div>
  );
}

export function ScorecardTable({ metrics, policy }: { metrics: Metric[]; policy: Policy[] }) {
  if (metrics.length === 0) return <p className="sc-mut">No rows: nothing on this set has been scored yet.</p>;
  return (
    <>
      <p className="sc-mut">Thresholds: {policy.map((p) => `${p.metric} ${p.comparator} ${p.value} (n ≥ ${p.min_n})`).join(" · ")}. {policy[0]?.citation}</p>
      <div className="sc-scroll">
        <table className="tbl sc-score">
          <thead><tr><th>Trait</th>{COLUMNS.map((c) => <th key={c.metric}>{c.label}</th>)}</tr></thead>
          <tbody>{traitsOf(metrics).map((t) => (
            <tr key={t.trait_id}><td>{t.label}</td>{COLUMNS.map((c) => {
              const row = judgedRow(metrics, t.trait_id, c.metric);
              const v = verdict(row, policy);
              const pct = c.metric.endsWith("_rate") || c.metric === "exact";
              return (
                <td key={c.metric} title={row?.unavailable_reason ?? (row ? `n = ${row.n}, against ${row.reference}` : "")}>
                  {row && row.available && row.value !== null && pct
                    ? `${Math.round(row.value * 100)}%` : cell(row, c.metric === "levels_used" ? 0 : 2)}
                  {v && v !== "not available" && <span className={"sc-v " + v.replace(/ /g, "-")}>{v}</span>}
                </td>);
            })}</tr>))}</tbody>
        </table>
      </div>
      <p className="sc-mut">Hover a cell for its n, its reference, or why it is not available.</p>
    </>
  );
}
